import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import {
  RefreshCw, Search, ArrowRight, AlertTriangle, Download, ExternalLink,
  PackageX, PackageSearch, Percent, Wallet, Clock
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

const fmtNumber = (n) => Number(n || 0).toLocaleString('tr-TR');
const fmtMoney = (n) => `₺${Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
const fmtPct = (n) => `%${(Number(n || 0) * 100).toFixed(1)}`;

const SORTS = [
  { key: 'price_desc', label: 'Fiyat (yüksek→düşük)' },
  { key: 'price_asc', label: 'Fiyat (düşük→yüksek)' },
  { key: 'brand_asc', label: 'Marka (A→Z)' },
  { key: 'title_asc', label: 'Ürün adı (A→Z)' },
];

const TABS = [
  { key: 'missing', label: 'Reklam feed’inde YOK', hint: 'Kaynakta var, reklama çıkmıyor', cls: 'text-red-700 border-red-500' },
  { key: 'stale', label: 'Kaynakta YOK (bayat)', hint: 'Reklam feed’inde duruyor, mağazada yok', cls: 'text-amber-700 border-amber-500' },
];

function StatCard({ icon: Icon, color, label, value, sub }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex items-center gap-2">
        <span className={`w-7 h-7 rounded-lg grid place-items-center ${color}`}><Icon className="w-4 h-4" /></span>
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
      </div>
      <div className="mt-1 text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
    </div>
  );
}

export default function High5FeedCompare() {
  const { selectedBrand } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [tab, setTab] = useState('missing');
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('ALL');
  const [catFilter, setCatFilter] = useState('ALL');
  const [sort, setSort] = useState('price_desc');

  const fetchData = async (force = false) => {
    if (!selectedBrand?.id) return;
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_BASE}/api/high5-feed-compare/summary${force ? '?refresh=1' : ''}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.error || d?.detail || `HTTP ${res.status}`);
      }
      setPayload(await res.json());
    } catch (e) {
      setPayload(null);
      setError(e?.message || 'Yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id]);

  const items = payload?.items?.[tab] || [];

  const brandOptions = useMemo(() => {
    const s = new Set();
    items.forEach((p) => p.brand && s.add(p.brand));
    return [...s].sort((a, b) => a.localeCompare(b, 'tr'));
  }, [items]);

  // Kategori = product_type'ın ilk iki seviyesi (ör. "Erkek > Erkek Spor Ayakkabı")
  const catOf = (p) => (p.productType || '').split('>').slice(0, 2).map((s) => s.trim()).filter(Boolean).join(' > ') || '—';
  const catOptions = useMemo(() => {
    const s = new Set();
    items.forEach((p) => s.add(catOf(p)));
    return [...s].sort((a, b) => a.localeCompare(b, 'tr'));
  }, [items]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let r = items.filter((p) => {
      if (brandFilter !== 'ALL' && p.brand !== brandFilter) return false;
      if (catFilter !== 'ALL' && catOf(p) !== catFilter) return false;
      if (!q) return true;
      return [p.title, p.brand, p.id, p.color, p.productType].some((v) => String(v || '').toLowerCase().includes(q));
    });
    const price = (p) => Number(p.salePrice ?? p.price ?? 0);
    const cmp = {
      price_desc: (a, b) => price(b) - price(a),
      price_asc: (a, b) => price(a) - price(b),
      brand_asc: (a, b) => (a.brand || '').localeCompare(b.brand || '', 'tr') || (a.title || '').localeCompare(b.title || '', 'tr'),
      title_asc: (a, b) => (a.title || '').localeCompare(b.title || '', 'tr'),
    }[sort];
    return [...r].sort(cmp);
  }, [items, search, brandFilter, catFilter, sort]);

  const rowsValue = useMemo(() => rows.reduce((s, p) => s + Number(p.salePrice ?? p.price ?? 0), 0), [rows]);

  const downloadCsv = () => {
    const head = ['id', 'marka', 'urun', 'kategori', 'renk', 'beden', 'fiyat', 'indirimli_fiyat', 'para_birimi', 'stok', 'link', 'gorsel'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [head.join(';')].concat(
      rows.map((p) => [p.id, p.brand, p.title, p.productType, p.color, p.size, p.price, p.salePrice, p.currency, p.availability, p.link, p.image].map(esc).join(';'))
    );
    // Excel'in Türkçe karakterleri doğru açması için BOM.
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `high5-feed-${tab === 'missing' ? 'reklamda-yok' : 'bayat'}-${(payload?.fetchedAt || '').slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const s = payload?.summary;
  const feeds = payload?.feeds;

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <Card className="rounded-2xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="p-5 border-b">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Feed Farkı — Kaynak ↔ Reklam</h1>
              <p className="text-sm text-gray-500 mt-1 max-w-3xl">
                High5’in iki Google Shopping XML’i canlı karşılaştırılır: mağaza altyapısının ürettiği
                <strong> kaynak feed (hebiar)</strong> ve Merchant Center’a giden
                <strong> optimize edilmiş reklam feed’i (crwizard)</strong>. Kaynakta olup reklam feed’ine
                düşmeyen ürün Google alışverişte hiç görünmez.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {payload?.fetchedAt && (
                <span className="hidden sm:inline-flex items-center gap-1 text-xs text-gray-400">
                  <Clock className="w-3.5 h-3.5" />
                  {new Date(payload.fetchedAt).toLocaleString('tr-TR')}
                  {payload.cached ? ' · önbellek' : ' · canlı'}
                </span>
              )}
              <button
                onClick={() => fetchData(true)}
                disabled={loading}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Feed’leri yeniden çek
              </button>
            </div>
          </div>

          {/* Feed karşılaştırma şeridi */}
          {feeds && (
            <div className="mt-5 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Kaynak feed · mağaza</div>
                <div className="mt-1 text-3xl font-bold text-emerald-800 tabular-nums">{fmtNumber(feeds.source.count)}</div>
                <div className="text-[11px] text-emerald-700/70 break-all mt-1">{feeds.source.url}</div>
              </div>
              <div className="grid place-items-center">
                <div className="flex items-center gap-2 text-gray-400">
                  <ArrowRight className="w-5 h-5" />
                  <span className="text-sm font-semibold text-red-600 tabular-nums">
                    −{fmtNumber(s?.missing || 0)}
                  </span>
                </div>
              </div>
              <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">Reklam feed’i · Merchant Center</div>
                <div className="mt-1 text-3xl font-bold text-blue-800 tabular-nums">{fmtNumber(feeds.optimized.count)}</div>
                <div className="text-[11px] text-blue-700/70 break-all mt-1">{feeds.optimized.url}</div>
              </div>
            </div>
          )}

          {/* Özet kartları */}
          {s && (
            <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard icon={PackageX} color="text-red-600 bg-red-50" label="Reklamda yok" value={fmtNumber(s.missing)} sub="kaynakta var, reklam feed’inde yok" />
              <StatCard icon={PackageSearch} color="text-amber-600 bg-amber-50" label="Bayat kayıt" value={fmtNumber(s.stale)} sub="reklamda var, kaynakta yok" />
              <StatCard icon={Percent} color="text-emerald-600 bg-emerald-50" label="Kapsama" value={fmtPct(s.coverage)} sub={`${fmtNumber(s.sourceTotal - s.missing)} / ${fmtNumber(s.sourceTotal)} ürün`} />
              <StatCard icon={Wallet} color="text-violet-600 bg-violet-50" label="Reklamsız liste değeri" value={fmtMoney(s.missingValue)} sub="eksik ürünlerin satış fiyatı toplamı" />
            </div>
          )}
        </div>

        {error && <div className="m-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}
        {!selectedBrand?.id && <div className="p-6 text-center text-gray-500">Lütfen bir marka seçin.</div>}
        {loading && !payload && <div className="p-8 text-center text-gray-400">Feed’ler çekiliyor…</div>}

        {/* Marka tablosu */}
        {payload?.brands?.length > 0 && (
          <div className="p-5 border-b">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <h2 className="text-sm font-semibold text-gray-700">Marka bazında fark</h2>
              <span className="text-xs text-gray-400">(satıra tıkla → aşağıdaki listeyi o markaya filtrele)</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <tr>
                    <th className="px-3 py-2.5 text-left">Marka</th>
                    <th className="px-3 py-2.5 text-right">Kaynak</th>
                    <th className="px-3 py-2.5 text-right">Reklam</th>
                    <th className="px-3 py-2.5 text-right">Reklamda yok</th>
                    <th className="px-3 py-2.5 text-right">Bayat</th>
                    <th className="px-3 py-2.5 text-left w-[220px]">Kapsama</th>
                    <th className="px-3 py-2.5 text-right">Reklamsız değer</th>
                  </tr>
                </thead>
                <tbody>
                  {payload.brands.map((b) => {
                    const active = brandFilter === b.brand;
                    const pct = Math.round((b.coverage || 0) * 100);
                    const bar = pct >= 95 ? 'bg-emerald-500' : pct >= 80 ? 'bg-amber-500' : 'bg-red-500';
                    return (
                      <tr
                        key={b.brand}
                        onClick={() => { setBrandFilter(active ? 'ALL' : b.brand); setTab('missing'); }}
                        className={`border-t border-gray-100 cursor-pointer ${active ? 'bg-blue-50' : 'hover:bg-blue-50/40'}`}
                      >
                        <td className="px-3 py-2 font-medium text-gray-800">{b.brand}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-600">{fmtNumber(b.source)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-600">{fmtNumber(b.optimized)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold">
                          {b.missing > 0 ? <span className="text-red-600">{fmtNumber(b.missing)}</span> : <span className="text-gray-300">0</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {b.stale > 0 ? <span className="text-amber-600">{fmtNumber(b.stale)}</span> : <span className="text-gray-300">0</span>}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden min-w-[80px]">
                              <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs tabular-nums text-gray-500 w-10 text-right">%{pct}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-600">{b.missingValue > 0 ? fmtMoney(b.missingValue) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold text-gray-800">
                    <td className="px-3 py-2.5">TOPLAM</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{fmtNumber(s?.sourceTotal)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{fmtNumber(s?.optimizedTotal)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-red-600">{fmtNumber(s?.missing)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-amber-600">{fmtNumber(s?.stale)}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-500">{fmtPct(s?.coverage)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{fmtMoney(s?.missingValue)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </Card>

      {/* Ürün listesi */}
      {payload && (
        <Card className="rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-5 pt-4 border-b">
            <div className="flex gap-6">
              {TABS.map((t) => {
                const count = payload?.items?.[t.key]?.length || 0;
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => { setTab(t.key); setBrandFilter('ALL'); setCatFilter('ALL'); }}
                    className={`pb-3 -mb-px border-b-2 text-sm font-semibold transition ${active ? t.cls : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                  >
                    {t.label} <span className="tabular-nums">({fmtNumber(count)})</span>
                    <div className="text-[11px] font-normal text-gray-400">{t.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Filtreler */}
          <div className="p-4 bg-gray-50/60 border-b flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Ürün, marka, kod, renk ara…"
                className="w-full pl-9 pr-3 py-2 bg-white border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} className="px-3 py-2 bg-white border rounded-xl text-sm">
              <option value="ALL">Tüm markalar</option>
              {brandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="px-3 py-2 bg-white border rounded-xl text-sm max-w-[260px]">
              <option value="ALL">Tüm kategoriler</option>
              {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 bg-white border rounded-xl text-sm">
              {SORTS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
            <button
              onClick={downloadCsv}
              disabled={rows.length === 0}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              <Download className="w-4 h-4" /> CSV indir
            </button>
            <span className="text-xs text-gray-400 ml-auto">
              {fmtNumber(rows.length)} ürün · {fmtMoney(rowsValue)}
            </span>
          </div>

          {/* Tablo */}
          {rows.length === 0 ? (
            <div className="p-10 text-center text-gray-400 text-sm">Bu filtrede ürün yok.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-3 text-left">Ürün</th>
                    <th className="px-3 py-3 text-left">Marka</th>
                    <th className="px-3 py-3 text-left">Kategori</th>
                    <th className="px-3 py-3 text-center">Beden</th>
                    <th className="px-3 py-3 text-right">Fiyat</th>
                    <th className="px-3 py-3 text-center w-10" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => {
                    const discounted = p.salePrice != null && p.price != null && p.salePrice < p.price;
                    return (
                      <tr key={`${tab}-${p.id}`} className="border-t border-gray-100 hover:bg-blue-50/30">
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-3">
                            {p.image
                              ? <img src={p.image} alt="" loading="lazy" className="w-10 h-10 rounded-lg object-cover border border-gray-100 bg-white" />
                              : <div className="w-10 h-10 rounded-lg bg-gray-100" />}
                            <div className="min-w-0">
                              <div className="font-medium text-gray-800 truncate max-w-[420px]">{p.title || p.id}</div>
                              <div className="text-xs text-gray-400">{p.color ? `${p.color} · ` : ''}{p.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{p.brand}</td>
                        <td className="px-3 py-2 text-gray-500 text-xs max-w-[240px] truncate">{p.productType || '—'}</td>
                        <td className="px-3 py-2 text-center text-gray-600">{p.size || '—'}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                          <div className="font-semibold text-gray-800">{fmtMoney(p.salePrice ?? p.price)}</div>
                          {discounted && <div className="text-[11px] text-gray-400 line-through">{fmtMoney(p.price)}</div>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {p.link && (
                            <a href={p.link} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-blue-600 inline-flex">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="px-5 py-3 text-[11px] text-gray-400 border-t bg-gray-50/60">
            Veri her istekte iki XML’den canlı okunur (15 dk önbellek). Kayıt birimi ürün+renk (id);
            her satır tek beden taşır. “Bayat kayıt” reklam feed’inde duran ama kaynakta artık bulunmayan üründür.
          </div>
        </Card>
      )}
    </div>
  );
}
